import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { HomeLink } from '../components/HomeLink';
import { useAuth } from '../contexts/AuthContext';
import { safeRedirect } from '../utils/safeRedirect';
import { useTenant } from '../contexts/TenantContext';
import { useTenantBranding } from '../contexts/TenantBrandingContext';
import { AlertCircle, Key } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { PoweredByCerebroBadge, PoweredByCerebroFooter } from '../components/PoweredByCerebro';
import axios from 'axios';
import { API_BASE } from '../config/api';

// Default Cerebro Logo - Teal/Gold Neural Brain Hexagon
const DEFAULT_LOGO = "https://customer-assets.emergentagent.com/job_cpd-compass/artifacts/km6w43n1_Teal%20Cerebro%20Transp.png";

export default function LoginPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTokenLogin, setShowTokenLogin] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  
  const [ssoStatus, setSsoStatus] = useState({ enabled: false, button_label: 'Sign in with Microsoft 365' });
  
  const { user, loading: authLoading, setUser } = useAuth();
  const { isTenant } = useTenant();
  const { branding } = useTenantBranding();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Branding panel settings (falls back to defaults)
  const logoUrl = branding?.logo_url || DEFAULT_LOGO;
  const orgName = branding?.site_name || 'Cerebro Core';

  // Microsoft 365 SSO button only shows when SSO is enabled + configured in admin
  useEffect(() => {
    axios.get(`${API_BASE}/api/m365-sso/status`)
      .then((res) => setSsoStatus(res.data || { enabled: false }))
      .catch(() => setSsoStatus({ enabled: false }));
  }, []);

  // Already signed in -> straight to where they were going (or the dashboard)
  useEffect(() => {
    if (!authLoading && user) {
      const target = searchParams.get('redirect');
      const safe = safeRedirect(target);
      navigate(['/register', '/signup', '/login'].some((p) => safe.startsWith(p)) ? '/dashboard' : safe, { replace: true });
    }
  }, [authLoading, user, searchParams, navigate]);

  // Errors returned by the SSO callback
  useEffect(() => {
    const ssoError = searchParams.get('sso_error');
    if (ssoError) setError(ssoError);
  }, [searchParams]);

  const handleTokenLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Use relative URL so it works on any deployment slot
      const response = await axios.post(
        `${API_BASE}/api/admin-access`,
        { access_token: accessToken }
      );
      
      const { access_token: token, user } = response.data;
      
      // Store token and user in localStorage
      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      
      // Update auth context
      if (setUser) {
        setUser(user);
      }
      
      toast.success('Admin access granted!');
      
      // Use window.location for hard redirect to ensure full page reload
      // This ensures AuthContext picks up the new token
      window.location.href = '/dashboard';
    } catch (err) {
      console.error('Token login error:', err);
      setError(err.response?.data?.detail || 'Invalid access token');
    } finally {
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = () => {
    setError('');
    setLoading(true);
    // Backend builds the Microsoft authorize URL (state + nonce + PKCE) and redirects
    const redirectTo = searchParams.get('redirect') || '/dashboard';
    window.location.href = `${API_BASE}/api/m365-sso/login?redirect_to=${encodeURIComponent(redirectTo)}`;
  };

  return (
    <div className="min-h-screen bg-navigate-dark flex flex-col" data-testid="login-page">
      <div className="flex flex-1">
        {/* Left Side - Form */}
        <div className="flex-1 flex items-center justify-center px-4 sm:px-6 lg:px-8">
          <div className="w-full max-w-md">
            {/* Logo */}
            <HomeLink className="flex items-center justify-center gap-3 mb-8">
              <img src={logoUrl} alt={orgName} className="h-16 w-auto" />
              <span 
                className="font-heading text-2xl font-bold text-navigate-gold"
                data-testid="login-site-name"
              >
                {orgName === 'Cerebro' ? 'Cerebro Core' : orgName}
              </span>
            </HomeLink>

            {/* Heading */}
            <div className="text-center mb-8">
              <h1 className="font-heading text-3xl font-bold text-white mb-2">
                Welcome to {orgName}
              </h1>
              <p className="text-zinc-400">
                Sign in to continue your learning journey
              </p>
            </div>

            {/* Error Alert */}
            {error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3 text-red-400">
                <AlertCircle className="h-5 w-5 shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Admin Token Login Form */}
            {showTokenLogin ? (
              <form onSubmit={handleTokenLogin} className="space-y-6">
                <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <p className="text-sm text-amber-400">
                    <Key className="h-4 w-4 inline mr-2" />
                    Admin Access Mode
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="accessToken" className="text-zinc-300">Access Token</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-500" />
                    <Input
                      id="accessToken"
                      type="password"
                      value={accessToken}
                      onChange={(e) => setAccessToken(e.target.value)}
                      placeholder="Enter your admin access token"
                      required
                      className="pl-10 bg-navigate-surface border-zinc-800 text-white placeholder:text-zinc-500 focus:border-navigate-gold focus:ring-navigate-gold"
                      data-testid="login-access-token"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-gold disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="token-login-submit"
                >
                  {loading ? 'Signing in...' : 'Access Dashboard'}
                </button>
                
                <button
                  type="button"
                  onClick={() => { setShowTokenLogin(false); setError(''); }}
                  className="w-full text-sm text-zinc-400 hover:text-white transition-colors"
                >
                  Back to login options
                </button>
              </form>
            ) : (
              /* Main Login Options */
              <div className="space-y-4">
                {/* Microsoft 365 SSO Button - only when enabled + configured in admin */}
                {ssoStatus.enabled && (
                  <>
                    <button
                      onClick={handleMicrosoftLogin}
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-3 px-4 py-4 bg-[#2F2F2F] hover:bg-[#3F3F3F] text-white rounded-lg border border-zinc-700 transition-colors disabled:opacity-50"
                      data-testid="microsoft-login-btn"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 21 21" fill="none">
                        <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
                        <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
                        <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
                        <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
                      </svg>
                      <span className="font-medium">{loading ? 'Redirecting...' : (ssoStatus.button_label || 'Sign in with Microsoft 365')}</span>
                    </button>

                    {/* Divider */}
                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-zinc-700"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-navigate-dark text-zinc-500">or</span>
                      </div>
                    </div>
                  </>
                )}

                {/* Admin Access Token Button */}
                <button
                  onClick={() => { setShowTokenLogin(true); setError(''); }}
                  className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-transparent hover:bg-zinc-800/50 text-amber-400 rounded-lg border border-amber-400/30 transition-colors"
                  data-testid="token-login-link"
                >
                  <Key className="h-5 w-5" />
                  <span>Admin Access Token</span>
                </button>
              </div>
            )}

            {/* Help Text */}
            <p className="mt-8 text-center text-sm text-zinc-500">
              Contact your administrator for account access
            </p>
            
            {/* Powered by Cerebro Core Badge - For tenants */}
            {isTenant && (
              <div className="mt-6 flex justify-center">
                <PoweredByCerebroBadge />
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Image (hidden on mobile) */}
        <div className="hidden lg:block lg:w-1/2 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-navigate-dark to-transparent z-10" />
          <img
            src={branding?.login_background_url || "https://images.unsplash.com/photo-1772631653221-9c77e6f82c9f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwxfHxkYXJrJTIwbW9kZXJuJTIwY29ycG9yYXRlJTIwYnVpbGRpbmd8ZW58MHx8fHwxNzc0NDQ4NjU5fDA&ixlib=rb-4.1.0&q=85"}
            alt="Corporate building"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 z-20 flex items-center justify-center">
            <div className="text-center max-w-md px-8">
              <h2 className="font-heading text-3xl font-bold text-white mb-4">
                Build Capability, Not Just Knowledge
              </h2>
              <p className="text-zinc-300">
                Join thousands of professionals advancing their careers with{' '}
                {orgName}, where learning meets intelligence.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <PoweredByCerebroFooter className="border-t border-zinc-800" />
    </div>
  );
}
